import type { ApiError as ApiErrorBody } from '@viaja/contracts';

/** Erro da API com mensagem pronta para a tela e mensagens por campo (validação). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * Único ponto de I/O do app com a API. Sempre same-origin, cookie de sessão httpOnly,
 * corpo JSON (exigido pela proteção CSRF da API).
 */
export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers:
        body !== undefined || method === 'POST' || method === 'PATCH'
          ? { 'content-type': 'application/json' }
          : {},
      body: body !== undefined ? JSON.stringify(body) : method === 'POST' ? '{}' : undefined,
    });
  } catch {
    throw new ApiError(0, 'offline', 'Sem conexão. Confira a internet e tente de novo.');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'http',
      err?.message ?? 'Algo deu errado. Tente de novo.',
      err?.fields,
    );
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const del = (path: string) => api<void>('DELETE', path);
